import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import ApexCharts from 'apexcharts';
import Chart from 'react-apexcharts';

import { downloadDataUri } from './helpers/insightsExport';

import { decimalUpTo } from 'Helpers/currencyFormat';

const BRAND = {
    blue: '#055DF5',
    blueTint: '#C1D7FD',
    green: '#00DD8D',
    charcoal: '#1B1C1D',
    charcoal50: '#8D8E8E',
};

// Categorical palette for the donut — every colour is saturated enough that the
// white % data-label and the tooltip text stay legible on it (the old palette
// had a near-white tint that disappeared on hover).
const DONUT_COLORS = [
    '#055DF5', // brand blue
    '#04A96D', // green
    '#7B61FF', // indigo
    '#F5841F', // orange
    '#E5484D', // red
    '#0FA3B1', // teal
    '#B5179E', // magenta
    '#6B7280', // slate
];

// Custom zoom levels. ApexCharts' native zoom toolbar only works for x/y line
// charts (not categorical bars or donuts), so zoom is implemented by re-rendering
// the chart at scaled dimensions inside a scrollable container — which works
// uniformly for every chart type and keeps tooltips correctly positioned.
const ZOOM_MIN = 1;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.5;
const BASE_HEIGHT = 300;

function toApexType(chartType) {
    if (chartType === 'donut') {
        return 'donut';
    }
    if (chartType === 'line') {
        return 'line';
    }

    return 'bar';
}

/**
 * Renders the current Insights result as a bar / line / donut chart, or a table.
 *
 * Chart-type switching is fully client-side — the rendered groups are reused, no
 * refetch. The donut type is unavailable when the measure is negativeCapable
 * (negative values cannot be drawn as a donut).
 *
 * Implementation notes (IB-644):
 * - Each mounted chart gets a UNIQUE `chart.id` (`insights-chart-<n>`). A fixed
 *   id meant switching type (bar→line→bar) remounted under an id whose previous,
 *   destroyed instance still lived in ApexCharts' global registry, so the new
 *   chart drew empty. A fresh id per mount avoids that.
 * - PNG export goes through `ApexCharts.exec(id, 'dataURI')`. react-apexcharts
 *   1.5.0 is a function component that does NOT forward its internal chart
 *   instance, so a component ref can't reach it — the global registry (keyed by
 *   our unique id) is the reliable handle.
 * - The Insights tab panel mounts hidden (Flowbite `display:none`), so a chart
 *   created then would measure zero width and render NaN geometry. A callback
 *   ref measures the container and (re)attaches a ResizeObserver every time it
 *   mounts — including after switching back from the table — and the chart is
 *   only rendered once the container has a real width.
 *
 * @ticket IB-644
 */
const InsightsChart = forwardRef(function InsightsChart({ result, chartType }, ref) {
    const realGroups = (result && result.groups) || [];
    const excludedGroups = (result && result.excludedGroups) || [];
    const isRatio = !!result && result.mode === 'ratio';
    const measureLabel = (result && result.measureLabel) || '';
    const denominatorLabel = (result && result.denominatorLabel) || '';

    // Left-off ratio groups (one-sided data) become 0-value, hoverable empty
    // columns at the end of a bar/line chart, so the user can hover them and read
    // why they were left off and the figure they do have. A 0-value donut slice is
    // invisible, so the donut lists them beneath the chart instead; the table
    // lists them as rows. Breakdown mode has no left-off groups.
    const chartGroups = useMemo(() => {
        if (!isRatio || chartType === 'donut' || chartType === 'table' || !excludedGroups.length) {
            return realGroups;
        }
        const zeros = excludedGroups.map((g) => ({
            key: `excluded-${g.key}`,
            label: g.label,
            value: 0,
            excluded: true,
            reason: g.reason,
            numerator: g.numerator,
            divisor: g.divisor,
        }));
        return [...realGroups, ...zeros];
    }, [isRatio, chartType, realGroups, excludedGroups]);

    const hasSeries = chartGroups.some((g) => Array.isArray(g.series) && g.series.length > 0);

    const labels = useMemo(() => chartGroups.map((g) => g.label), [chartGroups]);

    const apexType = toApexType(chartType);

    // A unique chart id per mount. Bump the sequence whenever the remount key
    // (type / data shape) changes, so each ApexCharts instance registers under
    // its own id and the export can target the live one.
    const remountKey = `${apexType}-${chartGroups.length}-${hasSeries ? 'split' : 'flat'}`;
    const seqRef = useRef(0);
    const prevKeyRef = useRef(null);
    if (prevKeyRef.current !== remountKey) {
        prevKeyRef.current = remountKey;
        seqRef.current += 1;
    }
    const chartId = `insights-chart-${seqRef.current}`;
    const idRef = useRef(chartId);
    idRef.current = chartId;

    const observerRef = useRef(null);
    const [width, setWidth] = useState(0);
    const [zoom, setZoom] = useState(ZOOM_MIN);

    // Reset zoom when the chart type or the underlying data shape changes.
    useEffect(() => {
        setZoom(ZOOM_MIN);
    }, [remountKey]);

    // Callback ref — runs whenever the container mounts/unmounts (e.g. toggling
    // to the table and back). Re-attaches the ResizeObserver to the live node so
    // the width is always tracked, and flips width from 0 → positive when the
    // panel becomes visible.
    const setContainer = useCallback((el) => {
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }

        if (!el) {
            return;
        }

        setWidth(el.clientWidth || 0);

        if (typeof ResizeObserver !== 'undefined') {
            const observer = new ResizeObserver((entries) => {
                const next = Math.floor(entries[0].contentRect.width);
                setWidth((prev) => (prev !== next ? next : prev));
            });
            observer.observe(el);
            observerRef.current = observer;
        }
    }, []);

    useImperativeHandle(
        ref,
        () => ({
            exportPng() {
                if (typeof ApexCharts === 'undefined' || !ApexCharts.exec) {
                    return;
                }
                Promise.resolve(ApexCharts.exec(idRef.current, 'dataURI'))
                    .then((res) => {
                        if (res && res.imgURI) {
                            downloadDataUri(res.imgURI, 'insights.png');
                        }
                    })
                    .catch(() => {});
            },
        }),
        [],
    );

    const { series, options } = useMemo(
        () =>
            buildChartConfig({
                groups: chartGroups,
                labels,
                chartType,
                hasSeries,
                apexType,
                chartId,
                isRatio,
                measureLabel,
                denominatorLabel,
            }),
        [chartGroups, labels, chartType, hasSeries, apexType, chartId, isRatio, measureLabel, denominatorLabel],
    );

    // The donut can't show a 0-value (left-off) slice, so list those groups
    // beneath it with the figure each one does have.
    const leftOffList =
        isRatio && chartType === 'donut' && excludedGroups.length > 0 ? (
            <LeftOffList groups={excludedGroups} measureLabel={measureLabel} denominatorLabel={denominatorLabel} />
        ) : null;

    if (!realGroups.length && !excludedGroups.length) {
        return <div className="tw-text-center tw-text-charcoal-50 tw-py-12 tw-text-sm">{$t('No data to display')}</div>;
    }

    if (chartType === 'table') {
        return (
            <InsightsTable
                groups={realGroups}
                excludedGroups={excludedGroups}
                hasSeries={hasSeries}
                measureLabel={measureLabel}
                denominatorLabel={denominatorLabel}
            />
        );
    }

    // Nothing plottable (e.g. a donut whose only groups were left off): show the
    // empty-state message, plus the donut's left-off list when present.
    if (!chartGroups.length) {
        return (
            <div>
                <div className="tw-text-center tw-text-charcoal-50 tw-py-12 tw-text-sm">{$t('No data to display')}</div>
                {leftOffList}
            </div>
        );
    }

    const zoomOut = () => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)));
    const zoomIn = () => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)));

    return (
        <div>
            <div className="tw-flex tw-items-center tw-justify-end tw-gap-1 tw-mb-2">
                <button
                    type="button"
                    onClick={zoomOut}
                    disabled={zoom <= ZOOM_MIN}
                    aria-label={$t('Zoom out')}
                    title={$t('Zoom out')}
                    className="tw-inline-flex tw-items-center tw-justify-center tw-h-7 tw-w-7 tw-rounded-md tw-border tw-border-charcoal-25 tw-text-charcoal-75 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed"
                >
                    −
                </button>
                <span className="tw-text-xs tw-text-charcoal-50 tw-w-10 tw-text-center tw-tabular-nums">{Math.round(zoom * 100)}%</span>
                <button
                    type="button"
                    onClick={zoomIn}
                    disabled={zoom >= ZOOM_MAX}
                    aria-label={$t('Zoom in')}
                    title={$t('Zoom in')}
                    className="tw-inline-flex tw-items-center tw-justify-center tw-h-7 tw-w-7 tw-rounded-md tw-border tw-border-charcoal-25 tw-text-charcoal-75 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed"
                >
                    +
                </button>
                <button
                    type="button"
                    onClick={() => setZoom(ZOOM_MIN)}
                    disabled={zoom === ZOOM_MIN}
                    className="tw-text-xs tw-text-blue tw-px-2 tw-py-1 disabled:tw-text-charcoal-50 disabled:tw-cursor-not-allowed"
                >
                    {$t('Reset')}
                </button>
            </div>

            {/* overflow:auto is only needed when zoomed (chart larger than the
                box). At 100% the chart fits, so keep overflow visible — otherwise
                the container clips a tooltip drawn above/beside the chart (e.g.
                the donut's top slice). */}
            <div ref={setContainer} className="tw-w-full" style={{ maxHeight: 560, overflow: zoom > ZOOM_MIN ? 'auto' : 'visible' }}>
                {width > 0 && (
                    <Chart
                        // Full remount on type / data-shape change so each ApexCharts
                        // instance is created fresh (and under a new unique id).
                        key={chartId}
                        options={options}
                        series={series}
                        type={apexType}
                        height={Math.round(BASE_HEIGHT * zoom)}
                        width={Math.round(width * zoom)}
                    />
                )}
            </div>
            {leftOffList}
        </div>
    );
});

export default InsightsChart;

function buildChartConfig({ groups, labels, chartType, hasSeries, apexType, chartId, isRatio, measureLabel, denominatorLabel }) {
    if (chartType === 'donut') {
        return {
            series: groups.map((g) => Math.abs(Number(g.value) || 0)),
            options: {
                chart: { id: chartId, type: 'donut', fontFamily: 'Outfit, sans-serif', toolbar: { show: false } },
                labels,
                colors: DONUT_COLORS,
                legend: { position: 'bottom' },
                dataLabels: { enabled: true },
                // Ratio mode shows the same calculation-order breakdown as the bar
                // and line charts (indexed by slice). Otherwise a neutral value
                // tooltip — not the slice colour, so the text stays readable on a
                // pale slice.
                // The chart sits in an overflow:auto container (for zoom), which
                // clips a tooltip drawn above a top slice. A fixed top-left
                // position keeps the box inside the canvas for every slice.
                tooltip: isRatio
                    ? {
                          theme: 'dark',
                          fillSeriesColor: false,
                          custom: ratioTooltip(groups, measureLabel, denominatorLabel, 'seriesIndex'),
                          fixed: { enabled: true, position: 'topLeft', offsetX: 0, offsetY: 0 },
                      }
                    : {
                          fillSeriesColor: false,
                          y: { formatter: (v) => formatNumber(v) },
                          fixed: { enabled: true, position: 'topLeft', offsetX: 0, offsetY: 0 },
                      },
            },
        };
    }

    // Numbers are bare (no currency code) per the wireframe; axis labels and
    // tooltips render with thousands separators and no decimals.
    const numberAxisTooltip = {
        yaxis: { labels: { formatter: (v) => formatNumber(v) } },
        tooltip: { shared: true, intersect: false, y: { formatter: (v) => formatNumber(v) } },
    };

    // ApexCharts auto-rotates bar x-axis labels but leaves line labels horizontal,
    // so on a line chart they overlap and most get hidden. rotateAlways + no
    // overlap-hiding gives the line the same diagonal labels as the bar.
    const categoryXaxis = {
        categories: labels,
        labels: { rotate: -45, rotateAlways: true, hideOverlappingLabels: false, style: { fontSize: '11px' } },
    };

    if (hasSeries) {
        const committed = groups.map((g) => seriesValue(g, 'Committed'));
        const uncommitted = groups.map((g) => seriesValue(g, 'Uncommitted'));

        return {
            series: [
                { name: $t('Committed'), data: committed },
                { name: $t('Uncommitted'), data: uncommitted },
            ],
            options: {
                chart: {
                    id: chartId,
                    type: apexType,
                    fontFamily: 'Outfit, sans-serif',
                    stacked: true,
                    toolbar: { show: false },
                },
                colors: [BRAND.blue, BRAND.blueTint],
                xaxis: categoryXaxis,
                yaxis: numberAxisTooltip.yaxis,
                legend: { position: 'bottom' },
                plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
                stroke: apexType === 'line' ? { curve: 'smooth', width: 3 } : { width: 0 },
                dataLabels: { enabled: false },
                // Trigger the tooltip anywhere in the category's column band, not
                // only when the pointer is directly over the (possibly tiny) bar.
                tooltip: numberAxisTooltip.tooltip,
            },
        };
    }

    return {
        series: [{ name: $t('Value'), data: groups.map((g) => Number(g.value) || 0) }],
        options: {
            chart: {
                id: chartId,
                type: apexType,
                fontFamily: 'Outfit, sans-serif',
                toolbar: { show: false },
            },
            colors: [BRAND.blue],
            xaxis: categoryXaxis,
            yaxis: numberAxisTooltip.yaxis,
            legend: { show: false },
            plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
            dataLabels: { enabled: false },
            stroke: apexType === 'line' ? { curve: 'smooth', width: 3 } : { width: 0 },
            // Ratio mode shows a calculation-order breakdown on hover
            // (numerator ÷ divisor = value); otherwise a plain formatted value.
            // intersect:false keeps the whole column hoverable (not just the bar);
            // followCursor anchors the box to the pointer so 0-height left-off
            // columns don't leave it stuck over the first column. shared MUST be
            // false here — ApexCharts ignores followCursor when shared is true.
            // theme:dark matches the donut tooltip.
            tooltip: isRatio
                ? { theme: 'dark', shared: false, intersect: false, followCursor: true, custom: ratioTooltip(groups, measureLabel, denominatorLabel) }
                : numberAxisTooltip.tooltip,
        },
    };
}

/**
 * Custom ratio hover tooltip, shared by the bar, line and donut charts so the
 * design is identical across all three. For a normal group it shows the label,
 * the measure ÷ denominator result, and the numerator/divisor that produced it.
 * For a left-off (one-sided) group it names the missing side and still shows the
 * figure the group does have. All numbers are bare and formatted (AC8).
 *
 * @param {string} indexKey  Which ApexCharts index to read — 'dataPointIndex'
 *                           for bar/line, 'seriesIndex' for the donut.
 */
function ratioTooltip(groups, measureLabel, denominatorLabel, indexKey = 'dataPointIndex') {
    // Self-contained dark box (charcoal, white text) so every chart type renders
    // an identical tooltip regardless of the chart's default theme, matching the
    // wireframe and the donut. Inline styles, not Tailwind classes, because this
    // HTML is injected by ApexCharts outside React's render tree.
    const box = 'background:#1B1C1D;color:#fff;border-radius:8px;padding:10px 12px;font-size:12px;line-height:1.5;box-shadow:0 6px 18px rgba(0,0,0,.25);';
    const muted = 'color:#cfd0d1;';

    return (ctx) => {
        const idx = ctx ? ctx[indexKey] : -1;
        const group = (idx != null && groups[idx]) || {};
        const measure = measureLabel || $t('Value');
        const denominator = denominatorLabel || $t('Denominator');

        const head = `<div style="font-weight:600;margin-bottom:4px;">${escapeHtml(group.label || '')}</div>`;
        const calc =
            `<div style="${muted}">` +
            `${escapeHtml(measure)} ${formatNumber(group.numerator)} ÷ ${escapeHtml(denominator)} ${formatNumber(group.divisor)}` +
            `</div>`;

        if (group.excluded) {
            const reason =
                group.reason === 'missing_numerator' ? $t('Left off — no budget tagged') : $t('Left off — no {{denominator}} to divide by', { denominator });

            return `<div style="${box}">${head}<div style="${muted}margin-bottom:4px;">${escapeHtml(reason)}</div>${calc}</div>`;
        }

        return (
            `<div style="${box}">${head}` +
            `<div style="color:#00DD8D;font-weight:600;margin-bottom:4px;">${escapeHtml(measure)} ${formatNumber(group.value)}</div>` +
            `${calc}</div>`
        );
    };
}

/**
 * Plain-language detail for a left-off group: names the missing side and shows
 * the figure the group does have. Used by the donut's left-off list and the
 * table's excluded rows.
 */
function excludedDetail(group, measureLabel, denominatorLabel) {
    const measure = measureLabel || $t('Budget');
    const denominator = denominatorLabel || $t('Denominator');

    if (group.reason === 'missing_numerator') {
        return $t('no budget tagged ({{denominator}} {{value}})', {
            denominator,
            value: formatNumber(group.divisor),
        });
    }

    return $t('no {{denominator}} ({{measure}} {{value}})', {
        denominator,
        measure,
        value: formatNumber(group.numerator),
    });
}

function LeftOffList({ groups, measureLabel, denominatorLabel }) {
    return (
        <div className="tw-mt-3 tw-text-xs">
            <div className="tw-font-medium tw-text-charcoal-75 tw-mb-1">{$t('Left off')}</div>
            <ul className="tw-text-charcoal-50 tw-space-y-0.5">
                {groups.map((group) => (
                    <li key={group.key}>
                        {group.label} — {excludedDetail(group, measureLabel, denominatorLabel)}
                    </li>
                ))}
            </ul>
        </div>
    );
}

function escapeHtml(value) {
    return String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function seriesValue(group, name) {
    const entry = (group.series || []).find((s) => s.name === name);
    return entry ? Number(entry.value) || 0 : 0;
}

function InsightsTable({ groups, excludedGroups = [], hasSeries, measureLabel = '', denominatorLabel = '' }) {
    return (
        <div className="tw-overflow-x-auto">
            <table className="tw-w-full tw-text-sm">
                <thead>
                    <tr className="tw-text-left tw-text-charcoal-75 tw-border-b tw-border-charcoal-25">
                        <th className="tw-py-2 tw-pr-4 tw-font-medium">{$t('Group')}</th>
                        <th className="tw-py-2 tw-pr-4 tw-font-medium tw-text-right">{$t('Value')}</th>
                        {hasSeries && <th className="tw-py-2 tw-pr-4 tw-font-medium tw-text-right">{$t('Committed')}</th>}
                        {hasSeries && <th className="tw-py-2 tw-font-medium tw-text-right">{$t('Uncommitted')}</th>}
                    </tr>
                </thead>
                <tbody>
                    {groups.map((group) => (
                        <tr key={group.key} className="tw-border-b tw-border-charcoal-15">
                            <td className="tw-py-2 tw-pr-4">{group.label}</td>
                            <td className="tw-py-2 tw-pr-4 tw-text-right">{formatNumber(group.value)}</td>
                            {hasSeries && <td className="tw-py-2 tw-pr-4 tw-text-right">{formatNumber(seriesValue(group, 'Committed'))}</td>}
                            {hasSeries && <td className="tw-py-2 tw-text-right">{formatNumber(seriesValue(group, 'Uncommitted'))}</td>}
                        </tr>
                    ))}
                    {/* Left-off groups (one-sided data) — shown with a dash so the
                        group is still visible but never plotted as a bar (AC9), with
                        the missing side named and the figure it has. */}
                    {excludedGroups.map((group) => (
                        <tr key={`excluded-${group.key}`} className="tw-border-b tw-border-charcoal-15 tw-text-charcoal-50">
                            <td className="tw-py-2 tw-pr-4">
                                {group.label}
                                <span className="tw-ml-2 tw-text-xs tw-italic">
                                    {$t('left off')} — {excludedDetail(group, measureLabel, denominatorLabel)}
                                </span>
                            </td>
                            <td className="tw-py-2 tw-pr-4 tw-text-right">—</td>
                            {hasSeries && <td className="tw-py-2 tw-pr-4 tw-text-right">—</td>}
                            {hasSeries && <td className="tw-py-2 tw-text-right">—</td>}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function formatNumber(value) {
    if (value == null || Number.isNaN(value)) {
        return '—';
    }
    return decimalUpTo(value, 0);
}
